import type { ReactNode } from 'react'
import { Component, type ErrorInfo } from 'react'
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom'
import { RequireAdminInner, RequireAuthInner, RequireCompanyInner } from 'shared/components/guards'
import AuthMeSync from './components/auth/AuthMeSync'
import RootLayout from './layouts/RootLayout'
import CustomerLayout from './layouts/CustomerLayout'
import AdminLayout from './layouts/AdminLayout'
import CompanyDashboardLayout from './components/company/Layout'
import LandingPage from './pages/customer/LandingPage'
import DashboardPage from './pages/customer/DashboardPage'
import DashboardLayout from './layouts/DashboardLayout'
import MyPaymentsPage from './pages/customer/MyPaymentsPage'
import CardsPage from './pages/customer/CardsPage'
import AddressesPage from './pages/customer/AddressesPage'
import RegisterPage from './pages/customer/RegisterPage'
import CompanyLogin from './pages/company/CompanyLogin'
import CompanyRegister from './pages/company/CompanyRegister'
import CompanyOverview from './pages/company/CompanyOverview'
import CompanyTransactions from './pages/company/CompanyTransactions'
import CompanyTransactionDetail from './pages/company/CompanyTransactionDetail'
import CompanyPayouts from './pages/company/CompanyPayouts'
import CompanyPayoutDetail from './pages/company/CompanyPayoutDetail'
import CompanyReports from './pages/company/CompanyReports'
import CompanySettings from './pages/company/CompanySettings'
import CompanyInternalReceipts from './pages/company/CompanyInternalReceipts'
import AdminLogin from './pages/admin/AdminLogin'
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminCompanies from './pages/admin/AdminCompanies'
import AdminCompanyDetail from './pages/admin/AdminCompanyDetail'
import AdminRegistrations from './pages/admin/AdminRegistrations'
import AdminPayments from './pages/admin/AdminPayments'
import AdminPayouts from './pages/admin/AdminPayouts'
import AdminAnalytics from './pages/admin/AdminAnalytics'
import AdminAuditLog from './pages/admin/AdminAuditLog'
import AdminUsers from './pages/admin/AdminUsers'
import AdminSettings from './pages/admin/AdminSettings'
import PolicyPage from './pages/customer/PolicyPage'

/** Redirect /pay/internal/:id or /payment/:id to / with modal open and the full payment URL for verification parsing. */
function NavigateToPayWithInternal() {
  const { id } = useParams<{ id: string }>()
  const paymentUrl = id ? `${window.location.origin}/payment/${id}` : ''
  return <Navigate to="/" state={{ openPaymentModal: true, preloadUrl: paymentUrl }} replace />
}

/** Protects /dashboard: requires any auth. Passes location + navigate so the guard never uses <Navigate> (avoids Router context / duplicate react-router issues). */
function DashboardRoute({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  return (
    <RequireAuthInner loginPath="/" fallbackPath="/" location={location} navigate={navigate}>
      {children}
    </RequireAuthInner>
  )
}

/** Protects /admin/*: requires customer token + me.is_admin. Passes location + navigate so the guard never uses Router hooks or <Navigate> (avoids duplicate react-router context errors). */
function AdminRoute({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  return (
    <RequireAdminInner loginPath="/" fallbackPath="/dashboard" location={location} navigate={navigate}>
      {children}
    </RequireAdminInner>
  )
}

/** Protects /company/* (except login/register): requires customer token + me.has_company. Passes location + navigate so the guard never uses Router hooks (avoids "useLocation invariant" when shared uses different react-router instance). */
function CompanyRoute({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  return (
    <RequireCompanyInner loginPath="/" fallbackPath="/dashboard" location={location} navigate={navigate}>
      {children}
    </RequireCompanyInner>
  )
}

/** Catches errors inside dashboard tree so we can show which part failed (and a clearer message). */
class DashboardErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null; componentStack: string | null }
> {
  state = { hasError: false, error: null as Error | null, componentStack: null as string | null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, componentStack: null }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null })
    console.error('Dashboard error boundary:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const err = this.state.error
      const msg = (err?.message && String(err.message).trim()) || '(no message)'
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6">
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Dashboard error</h1>
          <p data-testid="dashboard-error-message" className="text-slate-600 mb-2 font-mono text-sm max-w-lg break-all whitespace-pre-wrap">
            {msg}
          </p>
          {err?.stack && (
            <pre className="text-left text-xs text-slate-500 mb-2 max-h-32 overflow-auto max-w-lg whitespace-pre-wrap">
              {err.stack}
            </pre>
          )}
          {this.state.componentStack && (
            <pre data-testid="dashboard-error-stack" className="text-left text-xs text-slate-500 mb-4 max-h-40 overflow-auto max-w-lg whitespace-pre-wrap">
              {this.state.componentStack}
            </pre>
          )}
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null, componentStack: null })}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

/** Catches render errors so we show a message instead of a blank page. */
class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null; componentStack: string | null }
> {
  state = { hasError: false, error: null as Error | null, componentStack: null as string | null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, componentStack: null }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null })
    console.error('App error boundary:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const err = this.state.error
      const hasMsg = err && typeof err === 'object' && 'message' in err && String((err as Error).message).trim()
      const msg = hasMsg
        ? (err as Error).message
        : (err && typeof err === 'object' && 'stack' in err ? (err as Error).stack : null) || String(err) || 'Unknown error'
      const subtitle = !hasMsg && msg ? 'See details below.' : null
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6">
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Something went wrong</h1>
          {subtitle && <p className="text-slate-600 mb-2 text-sm">{subtitle}</p>}
          <p data-testid="app-error-message" className="text-slate-600 mb-4 font-mono text-sm max-w-lg break-all whitespace-pre-wrap">
            {msg}
          </p>
          {this.state.componentStack && (
            <pre data-testid="app-error-stack" className="text-left text-xs text-slate-500 mb-4 max-h-40 overflow-auto max-w-lg whitespace-pre-wrap">
              {this.state.componentStack}
            </pre>
          )}
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null, componentStack: null })}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <AppErrorBoundary>
    <BrowserRouter>
      <AuthMeSync />
      <Routes>
        <Route path="/" element={<RootLayout />}>
          {/* Customer: /, /payment → opens payment modal on /; /dashboard (requires auth) */}
          <Route element={<CustomerLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="payment" element={<Navigate to="/" state={{ openPaymentModal: true }} replace />} />
            <Route path="payment/:id" element={<NavigateToPayWithInternal />} />
            <Route path="pay/internal/:id" element={<NavigateToPayWithInternal />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="terms" element={<PolicyPage type="terms" />} />
            <Route path="privacy" element={<PolicyPage type="privacy" />} />
            <Route path="cookies" element={<PolicyPage type="cookies" />} />
            <Route path="refund-policy" element={<PolicyPage type="refund" />} />
            <Route path="dashboard" element={<DashboardRoute><DashboardErrorBoundary><DashboardLayout /></DashboardErrorBoundary></DashboardRoute>}>
              <Route index element={<DashboardPage />} />
              <Route path="payments" element={<MyPaymentsPage />} />
              <Route path="cards" element={<CardsPage />} />
              <Route path="addresses" element={<AddressesPage />} />
            </Route>
          </Route>
          {/* Company: /company/login, /company/register (public); all other /company/* require auth → redirect to /company/login */}
          <Route path="company">
            <Route path="login" element={<CompanyLogin />} />
            <Route path="register" element={<CompanyRegister />} />
            <Route
              element={
                <CompanyRoute>
                  <CompanyDashboardLayout />
                </CompanyRoute>
              }
            >
              <Route index element={<CompanyOverview />} />
              <Route path="transactions" element={<CompanyTransactions />} />
              <Route path="transactions/:id" element={<CompanyTransactionDetail />} />
              <Route path="payouts" element={<CompanyPayouts />} />
              <Route path="payouts/:id" element={<CompanyPayoutDetail />} />
              <Route path="reports" element={<CompanyReports />} />
              <Route path="internal-receipts" element={<CompanyInternalReceipts />} />
              <Route path="settings" element={<CompanySettings />} />
            </Route>
          </Route>
          {/* Admin: /admin/login (public), /admin/* (protected) */}
          <Route path="admin">
            <Route path="login" element={<AdminLogin />} />
            <Route
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="companies" element={<AdminCompanies />} />
              <Route path="companies/:id" element={<AdminCompanyDetail />} />
              <Route path="registrations" element={<AdminRegistrations />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="payouts" element={<AdminPayouts />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="audit" element={<AdminAuditLog />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
    </AppErrorBoundary>
  )
}
